import { useCallback, useEffect, useRef, useState } from 'react';
import type { tTourStep } from './Tour.types';

// Target elements render after their owning state update commits (e.g. a
// screen switch triggered by onBeforeShow), so polling — not a single
// synchronous check — is what lets a step wait for that re-render instead
// of skipping itself prematurely.
const TARGET_POLL_ATTEMPTS = 15;
const TARGET_POLL_INTERVAL_MS = 50;
// The last configured step has no follow-up step to cascade into if its
// target never appears (e.g. pagination with ≤6 flows) — stop() is the only
// outcome either way, so there's nothing to wait 750ms for. A short poll
// still gives it one last chance to catch a target that hasn't rendered yet,
// but keeps the blank gap before the tour ends barely noticeable.
const LAST_STEP_POLL_ATTEMPTS = 3;
// Spotlight re-measure when no animation frame runs (see the tracking effect).
const TRACK_FALLBACK_INTERVAL_MS = 100;
// The moments a target finishes moving: a menu's enter transition, a smooth
// scroll of any ancestor (scroll does not bubble, hence capture), and the tab
// coming back to the foreground after the page moved while it ran no frames.
const SETTLE_EVENTS = ['transitionend', 'animationend', 'scroll', 'visibilitychange'] as const;

// The spotlight rect: the target's box, widened to cover `expandTo` while
// that element exists (an open dropdown menu). Plain object on purpose: the
// tests' fake targets return plain rects too.
function spotlightRect(step: tTourStep, el: HTMLElement): DOMRect {
  const base = el.getBoundingClientRect();
  const extra = step.expandTo?.();
  if (!extra) return base;
  const more = extra.getBoundingClientRect();
  if (more.width === 0 && more.height === 0) return base;
  const left = Math.min(base.left, more.left);
  const top = Math.min(base.top, more.top);
  const right = Math.max(base.right, more.right);
  const bottom = Math.max(base.bottom, more.bottom);
  return { left, top, right, bottom, x: left, y: top, width: right - left, height: bottom - top } as DOMRect;
}

export type tUseTourReturn = {
  isActive: boolean;
  currentStepIndex: number;
  currentStep: tTourStep | null;
  targetRect: DOMRect | null;
  totalSteps: number;
  start: () => void;
  next: () => void;
  prev: () => void;
  skip: () => void;
};

// INT-1197 — lets analytics hook into completion/abandonment without every
// consumer instrumenting each step by hand. onStepEnter only fires for steps
// that actually resolve a target (the auto-skip path never counts), so
// `stepsShown` on completion reflects what the user really saw.
export type tUseTourAnalyticsCallbacks = {
  onStepEnter?: (step: tTourStep, index: number) => void;
  // `stepsShown` < `totalSteps` means the tour ended early: the remaining
  // steps auto-skipped because their target never rendered.
  // `endedByAction` is true when the last step ended through its
  // `advanceOn` (the person did the thing) rather than "Listo".
  // `lastStepShown` says whether the final step ever resolved a target: a
  // skipped middle step leaves it true, a tour that ran out of targets
  // before its end leaves it false.
  onComplete?: (stepsShown: number, totalSteps: number, endedByAction: boolean, lastStepShown: boolean) => void;
  onSkip?: (exitStepId: string | null, exitStepIndex: number) => void;
};

export default function useTour(
  steps: tTourStep[],
  callbacks?: tUseTourAnalyticsCallbacks,
): tUseTourReturn {
  const [isActive, setIsActive] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Ref (not a dependency) so a new callbacks object on every render never
  // recreates enterStep/skip and retriggers the target-polling effect.
  const callbacksRef = useRef(callbacks);
  callbacksRef.current = callbacks;
  const stepsShownRef = useRef<Set<number>>(new Set());

  const clearPoll = useCallback(() => {
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
  }, []);

  const stop = useCallback(() => {
    clearPoll();
    setIsActive(false);
    setTargetRect(null);
    setCurrentStepIndex(0);
  }, [clearPoll]);

  // Recursive by design: a step whose target never appears (e.g. an
  // existing flow with no single-choice component for step 2) skips
  // itself instead of showing a coachmark over nothing, which can
  // cascade through several steps in a row.
  const enterStep = useCallback(
    (index: number, viaAction = false) => {
      clearPoll();
      if (index >= steps.length) {
        callbacksRef.current?.onComplete?.(
          stepsShownRef.current.size,
          steps.length,
          viaAction,
          stepsShownRef.current.has(steps.length - 1),
        );
        stop();
        return;
      }
      const step = steps[index];
      // The person already performed the previous step's action: do not
      // repeat it on their behalf (see tTourStep.onBeforeShow).
      if (!viaAction) step.onBeforeShow?.();
      setTargetRect(null);

      const defaultAttempts = index === steps.length - 1 ? LAST_STEP_POLL_ATTEMPTS : TARGET_POLL_ATTEMPTS;
      // A step can ask for a longer wait (see tTourStep.targetTimeoutMs).
      const maxAttempts = step.targetTimeoutMs
        ? Math.ceil(step.targetTimeoutMs / TARGET_POLL_INTERVAL_MS)
        : defaultAttempts;
      let attempts = 0;
      const poll = () => {
        const el = step.getTarget();
        if (el) {
          // `block: 'nearest'` only scrolls when the target is fully out of
          // view, so a row that's already partially visible (e.g. half-cut
          // at the bottom of the viewport) never moves — 'center' forces it
          // fully into frame on every step/target change, not just the first
          // time. Optional chaining guards jsdom, which doesn't implement it.
          el.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
          stepsShownRef.current.add(index);
          callbacksRef.current?.onStepEnter?.(step, index);
          setCurrentStepIndex(index);
          setTargetRect(spotlightRect(step, el));
          // jsdom doesn't implement scrollIntoView — guarded so tests don't
          // need to stub it just to exercise a step transition.
          el.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
          return;
        }
        attempts += 1;
        if (attempts >= maxAttempts) {
          enterStep(index + 1);
          return;
        }
        pollTimeoutRef.current = setTimeout(poll, TARGET_POLL_INTERVAL_MS);
      };
      poll();
    },
    [steps, stop, clearPoll],
  );

  const start = useCallback(() => {
    if (steps.length === 0) return;
    stepsShownRef.current = new Set();
    setIsActive(true);
    enterStep(0);
  }, [steps, enterStep]);

  const next = useCallback(() => {
    enterStep(currentStepIndex + 1);
  }, [currentStepIndex, enterStep]);

  const prev = useCallback(() => {
    enterStep(Math.max(0, currentStepIndex - 1));
  }, [currentStepIndex, enterStep]);

  // Distinct from reaching the natural end (see enterStep's onComplete
  // branch above) — this is only ever invoked by an explicit user action
  // (the Tour component's "Saltar"/close control).
  const skipTour = useCallback(() => {
    const exitStep = steps[currentStepIndex] ?? null;
    // Stop first: whatever the consumer does on skip (analytics, closing
    // what the tour opened), the tour is over regardless of its outcome.
    stop();
    callbacksRef.current?.onSkip?.(exitStep?.id ?? null, currentStepIndex);
  }, [steps, currentStepIndex, stop]);

  // Escape is the emergency exit (NN heuristic 3): same as "Saltar", from
  // anywhere on the page while a tour is showing.
  useEffect(() => {
    if (!isActive) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        skipTour();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isActive, skipTour]);

  // Keeps the spotlight glued to its target every frame the step is showing
  // — not just on resize/scroll. A resize/scroll-only listener leaves the
  // hole stuck wherever the target happened to be when the tab last fired
  // one of those events; any other reflow (a web font finishing its swap,
  // an image loading, a CSS transition, the tab regaining focus) silently
  // desyncs the spotlight from the real element until the next resize/scroll.
  // A continuous rAF loop self-heals from all of those in ~16ms instead.
  // GRO-1247: rAF alone is not enough. A browser that runs no frames for the
  // page (a background or CDP-driven tab) leaves the spotlight on the rect
  // enterStep measured, which for a menu item is taken while the menu is
  // still entering and the smooth scroll has not started: the hole then
  // frames the row below "Configuración de la etapa", "Eliminar etapa". The
  // timer and the settle events below re-measure without a frame.
  useEffect(() => {
    if (!isActive) return undefined;
    const step = steps[currentStepIndex];
    if (!step) return undefined;
    let rafId = 0;
    let lastRectKey = '';
    const measure = () => {
      const el = step.getTarget();
      const rect = el ? spotlightRect(step, el) : null;
      const rectKey = rect ? `${rect.top}|${rect.left}|${rect.width}|${rect.height}` : '';
      if (rectKey !== lastRectKey) {
        lastRectKey = rectKey;
        setTargetRect(rect);
      }
    };
    const track = () => {
      measure();
      rafId = requestAnimationFrame(track);
    };
    rafId = requestAnimationFrame(track);
    const timerId = setInterval(measure, TRACK_FALLBACK_INTERVAL_MS);
    SETTLE_EVENTS.forEach((name) => document.addEventListener(name, measure, true));
    window.addEventListener('resize', measure);
    return () => {
      cancelAnimationFrame(rafId);
      clearInterval(timerId);
      SETTLE_EVENTS.forEach((name) => document.removeEventListener(name, measure, true));
      window.removeEventListener('resize', measure);
    };
  }, [isActive, currentStepIndex, steps]);

  // Advance-on-action (AGI-498): once the step's target is on screen, let
  // the step listen for its own action and move the tour along. Subscribed
  // after the render that showed the step, so the click that advanced the
  // previous step can never be caught twice; torn down on any way out.
  const hasTarget = targetRect !== null;
  useEffect(() => {
    if (!isActive || !hasTarget) return undefined;
    const step = steps[currentStepIndex];
    if (!step?.advanceOn) return undefined;
    let left = false;
    const unsubscribe = step.advanceOn({
      next: () => {
        if (left) return;
        left = true;
        enterStep(currentStepIndex + 1, true);
      },
      finish: () => {
        if (left) return;
        left = true;
        enterStep(steps.length, true);
      },
    });
    return () => {
      left = true;
      unsubscribe();
    };
  }, [isActive, hasTarget, currentStepIndex, steps, enterStep]);

  useEffect(() => clearPoll, [clearPoll]);

  return {
    isActive,
    currentStepIndex,
    currentStep: isActive ? steps[currentStepIndex] ?? null : null,
    targetRect,
    totalSteps: steps.length,
    start,
    next,
    prev,
    skip: skipTour,
  };
}
